/** UTM attribution: URL + sessionStorage, formatted block for admin emails */

export const UTM_STORAGE_KEY = 'antologia_utm';

export const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
];

function readUtmFromSearchParams(params) {
  const utm = {};
  UTM_KEYS.forEach((key) => {
    const val = params.get(key);
    if (val) utm[key] = val;
  });
  return utm;
}

function hasAnyUtm(utm) {
  return UTM_KEYS.some((key) => Boolean(utm?.[key]));
}

/** Persist first-touch UTM from URL (overwrite only when URL has utm_*). */
export function captureUtmParams() {
  if (typeof window === 'undefined') return;
  const fromUrl = readUtmFromSearchParams(new URLSearchParams(window.location.search));
  if (!hasAnyUtm(fromUrl)) return;
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(fromUrl));
  } catch {
    /* private mode / quota */
  }
}

function readUtmFromStorage() {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const utm = {};
    UTM_KEYS.forEach((key) => {
      if (parsed[key]) utm[key] = String(parsed[key]);
    });
    return utm;
  } catch {
    return {};
  }
}

/** Current UTM: query string first, then sessionStorage. */
export function getUtmParams() {
  if (typeof window === 'undefined') return {};
  const fromUrl = readUtmFromSearchParams(new URLSearchParams(window.location.search));
  if (hasAnyUtm(fromUrl)) return fromUrl;
  return readUtmFromStorage();
}

export function formatUtmAdminBlock(utm) {
  if (!utm || !hasAnyUtm(utm)) return '';
  const lines = ['--- UTM ---'];
  UTM_KEYS.forEach((key) => {
    if (utm[key]) lines.push(`${key}: ${utm[key]}`);
  });
  return `${lines.join('\n')}\n`;
}

export function appendUtmToAdminText(text, utm) {
  const block = formatUtmAdminBlock(utm ?? getUtmParams());
  if (!block) return text || '';
  const base = (text || '').trimEnd();
  return base ? `${base}\n\n${block.trimEnd()}\n` : block;
}

/** Page URL with UTM query restored from storage (for API source_url + backend fallback). */
export function getSourceUrlWithUtm() {
  if (typeof window === 'undefined') return '';
  const utm = getUtmParams();
  try {
    const url = new URL(window.location.href);
    UTM_KEYS.forEach((key) => {
      if (utm[key] && !url.searchParams.has(key)) {
        url.searchParams.set(key, utm[key]);
      }
    });
    return url.toString();
  } catch {
    return window.location.href;
  }
}

export function getUtmPayloadForApi() {
  return getUtmParams();
}
